import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import { DateTime } from 'luxon'
import { combineConfirmText, combineOrder, defaultCombineName, formatDuration } from '../../service/combine'

CombineDialog.propTypes = {
  uploads: PropTypes.arrayOf(PropTypes.object).isRequired,
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onCombine: PropTypes.func.isRequired
}

// "Combine videos": the chosen videos in recording order, which the owner can change, and an optional name.
// Rendered only while open, so every opening starts from the recorded order.
export default function CombineDialog ({ uploads, busy = false, onCancel, onCombine }) {
  const [order, setOrder] = useState(() => combineOrder(uploads))
  // Follows the first video until the owner types a name of their own.
  const [name, setName] = useState(null)
  const shownName = name ?? defaultCombineName(order)

  const move = (index, offset) => setOrder(current => {
    const next = [...current]
    ;[next[index], next[index + offset]] = [next[index + offset], next[index]]
    return next
  })

  return (
    <Dialog open onClose={busy ? undefined : onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Combine into one video?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">Playback order, earliest first. Move a video to change it.</Typography>
          <List dense disablePadding aria-label="Playback order">
            {order.map((upload, index) => (
              <ListItem
                key={upload.id}
                disableGutters
                secondaryAction={<>
                  <IconButton aria-label={`Move ${upload.name} up`} disabled={busy || index === 0} onClick={() => move(index, -1)}>
                    <ArrowUpwardRoundedIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label={`Move ${upload.name} down`} disabled={busy || index === order.length - 1} onClick={() => move(index, 1)}>
                    <ArrowDownwardRoundedIcon fontSize="small" />
                  </IconButton>
                </>}
                sx={{ pr: 12 }}
              >
                <ListItemText
                  primary={`${index + 1}. ${upload.name}`}
                  secondary={[
                    upload.date ? DateTime.fromISO(upload.date).toLocaleString(DateTime.DATETIME_MED) : 'Date not set',
                    upload.durationSeconds > 0 ? formatDuration(upload.durationSeconds) : null
                  ].filter(Boolean).join(' · ')}
                />
              </ListItem>
            ))}
          </List>
          <TextField
            label="Name (optional)"
            size="small"
            value={shownName}
            onChange={event => setName(event.target.value)}
            inputProps={{ maxLength: 255 }}
            disabled={busy}
            fullWidth
          />
          <Typography variant="body2">{combineConfirmText(order.length)}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => onCombine(order.map(upload => upload.id), shownName.trim() || null)} disabled={busy}>
          Combine
        </Button>
      </DialogActions>
    </Dialog>
  )
}
